/**
 * Sign-in attribution handoff.
 *
 * Google sign-in can finish in a different browser context from the one that
 * started it (for example, an in-app browser handing off to the system browser),
 * where the local attribution record does not exist. The OAuth redirect carries
 * the record in one opaque, namespaced parameter so analytics and ad tags never
 * read it as new campaign parameters. The receiving page restores it only when
 * it has no record of its own.
 */
import {
  AFFILIATE_ATTRIBUTION_MAX_AGE_MS,
  type AttributionData,
  hasAttributionSignal,
  isAffiliateReferralTouch,
  sanitizeAttributionUrl,
} from "@/lib/analytics/utm-tracker";

export const ATTRIBUTION_HANDOFF_PARAM = "ct_attr";
export const ATTRIBUTION_HANDOFF_MAX_CHARS = 1024;

const HANDOFF_VERSION = 1;
const FIELD_MAX_CHARS = 500;
// Transport freshness is independent of the age of the acquisition record.
const HANDOFF_MAX_AGE_MS = 30 * 60 * 1000;
const LEGACY_HANDOFF_MAX_ACQUISITION_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const HANDOFF_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

const HANDOFF_CAMPAIGN_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "gad_source",
  "gad_campaignid",
  "fbclid",
  "network",
  "ad_group",
  "keywords",
  "creative",
] as const;

// Dropped in this order when the encoded value would exceed the size limit.
const HANDOFF_URL_FIELDS = ["landing_page", "referrer"] as const;

function toBase64Url(text: string): string {
  let binary = "";
  new TextEncoder().encode(text).forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): string | null {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) return null;

  try {
    const padded =
      value.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((value.length + 3) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

function sanitizeHandoffUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;

  const sanitized = sanitizeAttributionUrl(value);
  if (!sanitized || sanitized.length > FIELD_MAX_CHARS) return undefined;

  try {
    const { protocol } = new URL(sanitized);
    return protocol === "https:" || protocol === "http:" ? sanitized : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Encode the transportable part of a stored record. Returns null when there is
 * no attribution signal to carry or the value cannot fit the size limit. Never
 * carries the private OpenAI Ads reference or latest-paid diagnostics.
 */
export function encodeAttributionHandoff(
  attribution: AttributionData | null | undefined
): string | null {
  if (!attribution || !hasAttributionSignal(attribution)) return null;

  const payload: Record<string, string | number> = { v: HANDOFF_VERSION, issued_at: Date.now() };
  for (const field of HANDOFF_CAMPAIGN_FIELDS) {
    const value = attribution[field];
    if (value) payload[field] = value.slice(0, FIELD_MAX_CHARS);
  }
  if (attribution.first_visit) payload.first_visit = attribution.first_visit;
  for (const field of HANDOFF_URL_FIELDS) {
    const value = sanitizeHandoffUrl(attribution[field]);
    if (value) payload[field] = value;
  }

  let encoded = toBase64Url(JSON.stringify(payload));
  for (const field of HANDOFF_URL_FIELDS) {
    if (encoded.length <= ATTRIBUTION_HANDOFF_MAX_CHARS) break;
    delete payload[field];
    encoded = toBase64Url(JSON.stringify(payload));
  }

  return encoded.length <= ATTRIBUTION_HANDOFF_MAX_CHARS ? encoded : null;
}

/**
 * Decode and validate a handoff value. Only allowlisted fields survive; anything
 * malformed, oversized, expired, or without an attribution signal yields null.
 */
export function decodeAttributionHandoff(
  value: string | null | undefined,
  now = Date.now()
): AttributionData | null {
  if (!value || value.length > ATTRIBUTION_HANDOFF_MAX_CHARS) return null;

  const json = fromBase64Url(value);
  if (!json) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

  const record = parsed as Record<string, unknown>;
  if (record.v !== HANDOFF_VERSION) return null;

  const hasIssuedAt = "issued_at" in record;
  if (hasIssuedAt) {
    if (
      typeof record.issued_at !== "number" ||
      !Number.isFinite(record.issued_at) ||
      record.issued_at > now + HANDOFF_FUTURE_TOLERANCE_MS ||
      now - record.issued_at > HANDOFF_MAX_AGE_MS
    ) {
      return null;
    }
  }

  const attribution: AttributionData = {};
  for (const field of HANDOFF_CAMPAIGN_FIELDS) {
    const fieldValue = record[field];
    if (typeof fieldValue === "string" && fieldValue && fieldValue.length <= FIELD_MAX_CHARS) {
      attribution[field] = fieldValue;
    }
  }
  for (const field of HANDOFF_URL_FIELDS) {
    const fieldValue = sanitizeHandoffUrl(record[field]);
    if (fieldValue) attribution[field] = fieldValue;
  }
  if (typeof record.first_visit === "string") {
    const firstVisitMs = Date.parse(record.first_visit);
    if (
      Number.isFinite(firstVisitMs) &&
      firstVisitMs <= now + HANDOFF_FUTURE_TOLERANCE_MS &&
      (hasIssuedAt || now - firstVisitMs <= LEGACY_HANDOFF_MAX_ACQUISITION_AGE_MS) &&
      (!isAffiliateReferralTouch(attribution) ||
        now - firstVisitMs <= AFFILIATE_ATTRIBUTION_MAX_AGE_MS)
    ) {
      attribution.first_visit = new Date(firstVisitMs).toISOString();
    } else {
      return null;
    }
  }

  return hasAttributionSignal(attribution) ? attribution : null;
}
